import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { useMakes } from 'common/hooks/useMakes';
import { useModels } from 'common/hooks/useModels';
import { selectDistanceOptions } from 'common/hooks/useSelectOptions';
import { formatters } from 'common/tools/formatters';
import { BodyTypeValues } from 'common/types/body-type';
import { ColorType, ColorTypeValues } from 'common/types/color-type';
import { ConditionType, ConditionTypeValues } from 'common/types/condition-type';
import { CylinderTypeValues } from 'common/types/cylinder-type';
import { DoorTypeValues } from 'common/types/door-type';
import { DrivetrainTypeValues } from 'common/types/drivetrain-type';
import { FuelTypeValues } from 'common/types/fuel-type';
import { MileageRange, MileageRangeValues } from 'common/types/mileage-range';
import { TitleStatus, TitleStatusValues } from 'common/types/title-status';
import { TransmissionTypeValues } from 'common/types/transmission-type';
import { BodyType, CylinderType, DoorType, DrivetrainType, FuelType, TransmissionType } from 'graphql/generated';
import _ from 'lodash';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cacheSelectors } from 'redux/cache.slice';
import { RootState } from 'redux/root-reducer';
import { searchParamActions, searchParamSelectors } from 'redux/search-param.slice';
import { AppDispatch } from 'redux/store';
import WsFlexbox from 'stories/layout/WsFlexbox';

const SearchChips = () => {
  const dispatch: AppDispatch = useDispatch();
  const { selectedMakes, removeMakeKey } = useMakes();
  const { getSelectedModels, removeModelKey } = useModels();
  const searchError = useSelector((state: RootState) => cacheSelectors.searchError(state));
  const filterCount = useSelector((state: RootState) => searchParamSelectors.filterCount(state));

  const bodys = useSelector((state: RootState) => searchParamSelectors.bodys(state));
  const colors = useSelector((state: RootState) => searchParamSelectors.colors(state));
  const conditions = useSelector((state: RootState) => searchParamSelectors.conditions(state));
  const cylinders = useSelector((state: RootState) => searchParamSelectors.cylinders(state));
  const doors = useSelector((state: RootState) => searchParamSelectors.doors(state));
  const drivetrains = useSelector((state: RootState) => searchParamSelectors.drivetrains(state));
  const fuels = useSelector((state: RootState) => searchParamSelectors.fuels(state));
  const mileage = useSelector((state: RootState) => searchParamSelectors.mileage(state));
  const titleSs = useSelector((state: RootState) => searchParamSelectors.titleSs(state));
  const transmissions = useSelector((state: RootState) => searchParamSelectors.transmissions(state));
  const yearMin = useSelector((state: RootState) => searchParamSelectors.yearMin(state));
  const yearMax = useSelector((state: RootState) => searchParamSelectors.yearMax(state));
  const priceMin = useSelector((state: RootState) => searchParamSelectors.priceMin(state));
  const priceMax = useSelector((state: RootState) => searchParamSelectors.priceMax(state));
  const distance = useSelector((state: RootState) => searchParamSelectors.distance(state));
  const zipCodeObj = useSelector((state: RootState) => searchParamSelectors.zipCodeObj(state));

  const distanceLabel = React.useMemo(() => {
    if (!(distance && zipCodeObj)) return '';
    return `${selectDistanceOptions.find(x => x.id === distance.toString())!.label} ${zipCodeObj.code.toString()}`;
  }, [distance, zipCodeObj]);

  const clearAllFilters = async () => {
    dispatch(searchParamActions.reset());
  };

  return (
    <>
      {!searchError && (
        <WsFlexbox direction="column" gap={1} padding={1} sx={{ paddingTop: 0 }}>
          <WsFlexbox
            padding={0}
            gap={0.5}
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
            }}
          >
            {filterCount > 1 ? (
              <Chip
                key={`chip-clear-all`}
                label={`Clear All`}
                onDelete={() => clearAllFilters()}
                color="primary"
                variant="filled"
              />
            ) : null}
            {/* {distance?.length ? (
              <Chip key={`chip-distance`} label={distanceLabel} color="primary" variant="outlined" />
            ) : null} */}
            {mileage?.length ? (
              <Chip
                key={`chip-mileage-range`}
                label={`${MileageRangeValues[mileage as MileageRange]} Miles`}
                onDelete={() => dispatch(searchParamActions.mileage())}
                color="primary"
                variant="outlined"
              />
            ) : null}
            {yearMin?.length ? (
              <Chip
                color="primary"
                variant="outlined"
                key={`chip-year-min`}
                label={`Min-Year: ${yearMin}`}
                onDelete={() => dispatch(searchParamActions.yearMin())}
              />
            ) : null}
            {yearMax?.length ? (
              <Chip
                color="primary"
                variant="outlined"
                key={`chip-year-max`}
                label={`Max-Year: ${yearMax}`}
                onDelete={() => dispatch(searchParamActions.yearMax())}
              />
            ) : null}
            {priceMin?.length ? (
              <Chip
                color="primary"
                variant="outlined"
                key={`chip-price-min`}
                label={`Min-Price: ${formatters.currency.format(+priceMin)}`}
                onDelete={() => dispatch(searchParamActions.priceMin(''))}
              />
            ) : null}
            {priceMax?.length ? (
              <Chip
                color="primary"
                variant="outlined"
                key={`chip-price-max`}
                label={`Max-Price: ${formatters.currency.format(+priceMax)}`}
                onDelete={() => dispatch(searchParamActions.priceMax(''))}
              />
            ) : null}
            {selectedMakes?.map(x => (
              <Box key={`chip-make-${x}`}>
                <Chip color="primary" variant="outlined" label={x} onDelete={() => removeMakeKey(x)} />
                {getSelectedModels(x).map(y => (
                  <Chip
                    color="primary"
                    variant="outlined"
                    key={`chip-${x}-model-${y}`}
                    label={y}
                    onDelete={() => removeModelKey(y, x)}
                  />
                ))}
              </Box>
            ))}
            {bodys?.map(x => (
              <Chip
                color="primary"
                variant="outlined"
                key={`chip-body-type-${x}`}
                label={BodyTypeValues[x as BodyType]}
                onDelete={() => dispatch(searchParamActions.removeArrayKey({ key: 'bodys', val: x }))}
              />
            ))}
            {colors?.map(x => (
              <Chip
                color="primary"
                variant="outlined"
                key={`chip-color-${x}`}
                label={`Color: ${ColorTypeValues[x as ColorType]}`}
                onDelete={() => dispatch(searchParamActions.removeArrayKey({ key: 'colors', val: x }))}
              />
            ))}
            {titleSs?.map(x => (
              <Chip
                color="primary"
                variant="outlined"
                key={`chip-title-status-${x}`}
                label={`${TitleStatusValues[x as TitleStatus]} Title`}
                onDelete={() => dispatch(searchParamActions.removeArrayKey({ key: 'titleSs', val: x }))}
              />
            ))}
            {conditions?.map(x => (
              <Chip
                color="primary"
                variant="outlined"
                key={`chip-condition-${x}`}
                label={`${ConditionTypeValues[x as ConditionType]} Condition`}
                onDelete={() => dispatch(searchParamActions.removeArrayKey({ key: 'conditions', val: x }))}
              />
            ))}
            {doors?.map(x => (
              <Chip
                color="primary"
                variant="outlined"
                key={`chip-doorType-${x}`}
                label={DoorTypeValues[x as DoorType]}
                onDelete={() => dispatch(searchParamActions.removeArrayKey({ key: 'doors', val: x }))}
              />
            ))}
            {drivetrains?.map(x => (
              <Chip
                color="primary"
                variant="outlined"
                key={`chip-drivetrainType-${x}`}
                label={x === 'other' ? `Drivetrain: Other` : DrivetrainTypeValues[x as DrivetrainType]}
                onDelete={() => dispatch(searchParamActions.removeArrayKey({ key: 'drivetrains', val: x }))}
              />
            ))}
            {cylinders?.map(x => (
              <Chip
                color="primary"
                variant="outlined"
                key={`chip-cylinderType-${x}`}
                label={
                  x === 'na' || x === 'rotary'
                    ? `${CylinderTypeValues[x as CylinderType]} Cylinders`
                    : CylinderTypeValues[x as CylinderType]
                }
                onDelete={() => dispatch(searchParamActions.removeArrayKey({ key: 'cylinders', val: x }))}
              />
            ))}
            {fuels?.map(x => (
              <Chip
                color="primary"
                variant="outlined"
                key={`chip-fuelType-${x}`}
                label={FuelTypeValues[x as FuelType]}
                onDelete={() => dispatch(searchParamActions.removeArrayKey({ key: 'fuels', val: x }))}
              />
            ))}
            {transmissions?.map(x => (
              <Chip
                color="primary"
                variant="outlined"
                key={`chip-transmissionType-${x}`}
                label={
                  x === 'na'
                    ? `${TransmissionTypeValues[x as TransmissionType]} Transmission`
                    : TransmissionTypeValues[x as TransmissionType]
                }
                onDelete={() => dispatch(searchParamActions.removeArrayKey({ key: 'transmissions', val: x }))}
              />
            ))}
          </WsFlexbox>
        </WsFlexbox>
      )}
    </>
  );
};

export default SearchChips;
